import { useMemo, useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { medicationService } from '../services/medicationService'
import { PatientMedication } from '../types/medication'
import { MaskedField } from './MaskedField'
import './PatientMedications.css'

interface PatientMedicationsProps {
  patientId: string
  readOnly?: boolean
}

export function PatientMedications({ patientId, readOnly = false }: PatientMedicationsProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<'active' | 'all'>('active')
  const canWrite = !readOnly
  const currentListRef = useRef<HTMLDivElement | null>(null)
  const pastListRef = useRef<HTMLDivElement | null>(null)
  const [currentScrollState, setCurrentScrollState] = useState({
    hasOverflow: false,
    atTop: true,
    atBottom: true,
  })
  const [pastScrollState, setPastScrollState] = useState({
    hasOverflow: false,
    atTop: true,
    atBottom: true,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['patient-medications', patientId, statusFilter],
    queryFn: () =>
      medicationService.getPatientMedications(patientId, {
        status: statusFilter === 'active' ? 'active' : undefined,
      }),
  })

  const markErrorMutation = useMutation({
    mutationFn: ({ medicationId, reason }: { medicationId: string; reason?: string }) =>
      medicationService.markMedicationError(patientId, medicationId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['patient-medications', patientId],
        exact: false 
      })
    },
  })

  const orderFromListMutation = useMutation({
    mutationFn: (medicationId: string) => medicationService.orderFromList(patientId, medicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['patient-medications', patientId],
        exact: false 
      })
    },
  })

  const stopMedicationMutation = useMutation({
    mutationFn: ({ medicationId, endDate }: { medicationId: string; endDate: string }) =>
      medicationService.updatePatientMedication(patientId, medicationId, {
        status: 'stopped',
        endDate,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['patient-medications', patientId],
        exact: false 
      })
    },
  })

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  const currentMedications = useMemo(
    () => (data?.items || []).filter((m) => m.status === 'active' && !m.isDoneInError),
    [data],
  )

  const pastMedications = useMemo(
    () => (data?.items || []).filter((m) => m.status !== 'active' || m.isDoneInError),
    [data],
  )

  const updateScrollState = (
    ref: React.MutableRefObject<HTMLDivElement | null>,
    setState: React.Dispatch<
      React.SetStateAction<{ hasOverflow: boolean; atTop: boolean; atBottom: boolean }>
    >,
  ) => {
    const el = ref.current
    if (!el) return
    const hasOverflow = el.scrollHeight > el.clientHeight + 1
    const atTop = el.scrollTop <= 1
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    setState({ hasOverflow, atTop, atBottom })
  }

  useEffect(() => {
    const handleResize = () => {
      updateScrollState(currentListRef, setCurrentScrollState)
      updateScrollState(pastListRef, setPastScrollState)
    }

    handleResize()

    const currentEl = currentListRef.current
    const pastEl = pastListRef.current
    const resizeObserver = new ResizeObserver(handleResize)

    if (currentEl) {
      currentEl.addEventListener('scroll', handleResize)
      resizeObserver.observe(currentEl)
    }
    if (pastEl) {
      pastEl.addEventListener('scroll', handleResize)
      resizeObserver.observe(pastEl)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (currentEl) currentEl.removeEventListener('scroll', handleResize)
      if (pastEl) pastEl.removeEventListener('scroll', handleResize)
      resizeObserver.disconnect()
    }
  }, [currentMedications.length, pastMedications.length, statusFilter])

  const handleMarkError = async (medication: PatientMedication) => {
    if (!canWrite) return
    const reason = window.prompt(
      `Mark "${medication.name}" as done in error.\n\nPlease enter a reason (optional):`,
    )
    try {
      await markErrorMutation.mutateAsync({ medicationId: medication.medicationId, reason: reason || undefined })
    } catch (err) {
      console.error('Failed to mark medication as error', err)
      window.alert('Failed to mark medication as done in error.')
    }
  }

  const handleOrderFromList = async (medication: PatientMedication) => {
    if (!canWrite) return
    try {
      await orderFromListMutation.mutateAsync(medication.medicationId)
      window.alert('Medication marked as ordered from list.')
    } catch (err) {
      console.error('Failed to mark medication ordered from list', err)
      window.alert('Failed to order medication from list.')
    }
  }

  const handleStopMedication = async (medication: PatientMedication) => {
    if (!canWrite) return
    const today = new Date().toISOString().split('T')[0]
    const endDate = window.prompt(
      `Stop "${medication.name}" and move to past medications.\n\nEnter end date (YYYY-MM-DD) or leave blank for today (${today}):`,
      today,
    )
    if (endDate === null) return // User cancelled

    const finalEndDate = endDate.trim() || today
    try {
      await stopMedicationMutation.mutateAsync({
        medicationId: medication.medicationId,
        endDate: finalEndDate,
      })
    } catch (err) {
      console.error('Failed to stop medication', err)
      window.alert('Failed to stop medication.')
    }
  }

  if (isLoading) {
    return <div className="medications-loading">Loading medications...</div>
  }

  if (error) {
    return (
      <div className="medications-error" role="alert">
        Unable to load medications. Please try again.
      </div>
    )
  }

  const totalCount = data?.totalCount || 0
  const visibleCount = (data?.items || []).length
  const hasOverflow = totalCount > visibleCount

  return (
    <div className="patient-medications" aria-label="Patient medications list">
      <div className="medications-header-row">
        <div>
          <h2>Medications</h2>
          <p className="medications-subtitle">
            Current and historical medications recorded for this patient.
          </p>
        </div>
        <div className="medications-filters">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/patients/${patientId}/medications/print`)}
          >
            Print list
          </button>
          <label htmlFor="med-status-filter">View:</label>
          <select
            id="med-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'active' | 'all')}
          >
            <option value="active">Current only</option>
            <option value="all">All (including past & errors)</option>
          </select>
        </div>
      </div>

      {hasOverflow && (
        <div className="medications-overflow-banner" role="status">
          Showing {visibleCount} of {totalCount} medications. Additional medications may not be visible on screen or printouts.
        </div>
      )}

      <div className="medications-section">
        <h3>Current medications</h3>
        <MaskedField
          field="medications"
          value={currentMedications}
          patientId={patientId}
          confidentialityLevel="restricted"
        >
          {(canAccess, maskedValue, requestEmergencyAccess) => {
            if (!canAccess) {
              return (
                <div className="medications-empty">
                  <p>You do not have permission to view medication details.</p>
                  {requestEmergencyAccess && (
                    <button
                      onClick={requestEmergencyAccess}
                      className="btn-secondary btn-small"
                      style={{ marginTop: '1rem' }}
                    >
                      Request Emergency Access
                    </button>
                  )}
                </div>
              )
            }

            if (maskedValue.length === 0) {
              return <div className="medications-empty">No active medications recorded.</div>
            }

            const showCurrentOverflowCue =
              currentScrollState.hasOverflow || maskedValue.length > 8

            return (
              <>
                {showCurrentOverflowCue && (
                  <div className="medications-scroll-hint" role="status">
                    Scroll to view more medications.
                  </div>
                )}
                <div
                  ref={currentListRef}
                  className={[
                    'medications-table-wrap',
                    showCurrentOverflowCue ? 'has-overflow' : '',
                    currentScrollState.atTop ? 'at-top' : '',
                    currentScrollState.atBottom ? 'at-bottom' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <table className="medications-table">
                    <thead>
                      <tr>
                        <th>Medication</th>
                        <th>Dose</th>
                        <th>Route</th>
                        <th>Frequency</th>
                        <th>Quantity</th>
                        <th>Indication</th>
                        <th>Prescriber</th>
                        <th>Start date</th>
                        <th>Added</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maskedValue.map((m: PatientMedication) => (
                        <tr key={m.medicationId}>
                          <td>
                            <strong>{m.name}</strong>
                          </td>
                          <td>
                            {m.doseText || (m.dose ? `${m.dose.value ?? ''} ${m.dose.unit ?? ''}`.trim() : '-')}
                          </td>
                          <td>{m.route || '-'}</td>
                          <td>{m.frequency?.text || m.frequency?.code || '-'}</td>
                          <td>
                            {m.quantity
                              ? `${m.quantity.value ?? ''} ${m.quantity.unit ?? ''}`.trim()
                              : '-'}
                          </td>
                          <td>
                            {m.indication ? (
                              <span className="medications-indication-badge">{m.indication}</span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>{m.prescriberName}</td>
                          <td>{formatDate(m.startDate)}</td>
                          <td className="medications-date-muted">{formatDate(m.createdAt)}</td>
                          <td>
                            <div className="medications-actions">
                              <button
                                className="btn-link small"
                                onClick={() => handleOrderFromList(m)}
                                disabled={orderFromListMutation.isPending || !canWrite}
                                title={canWrite ? 'Mark as ordered from list' : 'Secondary records are read-only'}
                              >
                                Order
                              </button>
                              <button
                                className="btn-link small"
                                onClick={() => handleStopMedication(m)}
                                disabled={stopMedicationMutation.isPending || !canWrite}
                                title={canWrite ? 'Stop medication and move to past' : 'Secondary records are read-only'}
                              >
                                Stop
                              </button>
                              <button
                                className="btn-link small danger"
                                onClick={() => handleMarkError(m)}
                                disabled={markErrorMutation.isPending || !canWrite}
                                title={canWrite ? 'Mark as done in error' : 'Secondary records are read-only'}
                              >
                                Error
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )
          }}
        </MaskedField>
      </div>

      {statusFilter === 'all' && (
        <div className="medications-section">
          <h3>Past & error medications</h3>
          <MaskedField
            field="medications"
            value={pastMedications}
            patientId={patientId}
            confidentialityLevel="restricted"
          >
            {(canAccess, maskedPastMedications, requestEmergencyAccess) => {
              if (!canAccess) {
                return (
                  <div className="medications-empty">
                    <p>You do not have permission to view medication details.</p>
                    {requestEmergencyAccess && (
                      <button
                        onClick={requestEmergencyAccess}
                        className="btn-secondary btn-small"
                        style={{ marginTop: '1rem' }}
                      >
                        Request Emergency Access
                      </button>
                    )}
                  </div>
                )
              }

              if (maskedPastMedications.length === 0) {
                return <div className="medications-empty">No past or error medications recorded.</div>
              }

              const showPastOverflowCue =
                pastScrollState.hasOverflow || maskedPastMedications.length > 8

              return (
                <>
                  {showPastOverflowCue && (
                    <div className="medications-scroll-hint" role="status">
                      Scroll to view more medications.
                    </div>
                  )}
                  <div
                    ref={pastListRef}
                    className={[
                      'medications-table-wrap',
                      showPastOverflowCue ? 'has-overflow' : '',
                      pastScrollState.atTop ? 'at-top' : '',
                      pastScrollState.atBottom ? 'at-bottom' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <table className="medications-table condensed">
                      <thead>
                        <tr>
                          <th>Medication</th>
                          <th>Dose / Route</th>
                          <th>Indication</th>
                          <th>Status</th>
                          <th>Prescriber</th>
                          <th>Start date</th>
                          <th>End date</th>
                          <th>Added</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {maskedPastMedications.map((m: PatientMedication) => (
                          <tr key={m.medicationId}>
                            <td>
                              <strong>{m.name}</strong>
                            </td>
                            <td>
                              <div className="medications-detail-line">
                                {m.doseText || (m.dose ? `${m.dose.value ?? ''} ${m.dose.unit ?? ''}`.trim() : '-')}
                              </div>
                              {m.route && <div className="medications-detail-line muted">{m.route}</div>}
                            </td>
                            <td>
                              {m.indication ? (
                                <span className="medications-indication-badge">{m.indication}</span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>
                              <span className={`badge badge-${m.status}`}>
                                {m.isDoneInError ? 'Done in error' : m.status}
                              </span>
                            </td>
                            <td>{m.prescriberName}</td>
                            <td>{formatDate(m.startDate)}</td>
                            <td>
                              {m.isDoneInError && m.errorMarkedAt
                                ? formatDate(m.errorMarkedAt)
                                : formatDate(m.endDate)}
                            </td>
                            <td className="medications-date-muted">{formatDate(m.createdAt)}</td>
                            <td>
                              {m.isDoneInError && m.errorReason && (
                                <div className="medications-error-reason">
                                  <strong>Error:</strong> {m.errorReason}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            }}
          </MaskedField>
        </div>
      )}
    </div>
  )
}

